import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * Capability tokens that the backend recognises via the `X-Rovo-Chat-Capabilities`
 * header. Each capability opts the request into a piece of backend behaviour.
 */
export const STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY = 'store-message-from-user-settings';
/** Inline actionable follow-ups: questions are woven into prose. */
export const ACTIONABLE_FOLLOW_UPS_CAPABILITY = 'actionable-follow-ups';
/** Stacked actionable follow-ups: questions returned as a plain list, no prose. */
export const ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY = 'actionable-follow-ups-stacked';

/**
 * Maps each gated capability to the gate/experiment that enables it. Add new
 * capability/gate pairs here rather than introducing nested ternaries at the
 * call sites.
 */
const GATED_CAPABILITIES: ReadonlyArray<{ capability: string; isEnabled: () => boolean }> = [
	{
		capability: STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY,
		isEnabled: () => fg('ai_mate_store_messages_be_driven'),
	},
];

/**
 * The three cohorts of the actionable follow-ups experiment. `control` receives no
 * follow-ups; `inline` and `stacked` each request a different backend prompt/format
 * via their respective capability header.
 */
export type ActionableFollowUpsCohort = 'control' | 'inline' | 'stacked';

/** Maps an experiment cohort to the capability header it opts the request into. */
const ACTIONABLE_FOLLOW_UPS_CAPABILITY_BY_COHORT: Record<
	ActionableFollowUpsCohort,
	string | undefined
> = {
	control: undefined,
	inline: ACTIONABLE_FOLLOW_UPS_CAPABILITY,
	stacked: ACTIONABLE_FOLLOW_UPS_STACKED_CAPABILITY,
};

/**
 * Resolves the actionable follow-ups experiment cohort and FIRES an exposure.
 *
 * Exposure is fired here — at the point we decide which capability header to send —
 * so that all cohorts (including `control`, which never receives a `◈` marker from
 * the backend) are exposed equally. Firing on client-side marker detection instead
 * would never expose the control cohort and would imbalance the experiment.
 */
const resolveActionableFollowUpsCohort = (): ActionableFollowUpsCohort =>
	FeatureGates.getExperimentValue<ActionableFollowUpsCohort>(
		'rovo_chat_actionable_follow_ups_exp',
		'cohort',
		'control',
	);

/**
 * Builds the effective list of capabilities to send with a chat message by
 * appending any gate-enabled capabilities to the caller-provided list.
 *
 * Behaviour is preserved from the previous inline logic:
 * - When no gated capabilities are enabled, the original `capabilities` value is
 *   returned untouched (including `undefined`), so the header is only sent when
 *   there is something to send.
 * - The result is de-duplicated to avoid sending the same capability twice.
 *
 * The actionable follow-ups experiment contributes its cohort-specific capability
 * (`actionable-follow-ups` for `inline`, `actionable-follow-ups-stacked` for
 * `stacked`, nothing for `control`).
 */
export const buildEffectiveCapabilities = (capabilities?: string[]): string[] | undefined => {
	const enabledCapabilities = GATED_CAPABILITIES.filter(({ isEnabled }) => isEnabled()).map(
		({ capability }) => capability,
	);

	const followUpsCapability =
		ACTIONABLE_FOLLOW_UPS_CAPABILITY_BY_COHORT[resolveActionableFollowUpsCohort()];
	if (followUpsCapability) {
		enabledCapabilities.push(followUpsCapability);
	}

	if (enabledCapabilities.length === 0) {
		return capabilities;
	}

	return Array.from(new Set([...(capabilities ?? []), ...enabledCapabilities]));
};
